import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthToken } from '../services/apiClient';
import {
  getAllIngredients,
  getIngredientProducts,
  addProductToIngredient,
  removeProductFromIngredient,
  updateIngredient,
  IngredientDto,
  IngredientProductDto,
  Allergens,
} from '../services/ingredients';
import { getAllProducts, ProductDto } from '../services/products';
import { Units } from '../services/foods';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import TextField from '@mui/material/TextField';
import Collapse from '@mui/material/Collapse';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import Switch from '@mui/material/Switch';

function formatUnits(units: Units): string {
  switch (units) {
    case Units.Piece: return 'ks';
    case Units.Liter: return 'l';
    case Units.Pinch: return 'špetka';
    case Units.Deciliter: return 'dl';
    case Units.Clove: return 'stroužek';
    case Units.Kilogram: return 'kg';
    case Units.Gram: return 'g';
    case Units.TableSpoon: return 'lžíce';
    case Units.TeaSpoon: return 'lžička';
    case Units.Milliliter: return 'ml';
    case Units.Handful: return 'hrst';
    case Units.Sprig: return 'snítka';
    case Units.Crucible: return 'kelímek';
    case Units.Can: return 'plechovka';
    case Units.Slice: return 'plátek';
    case Units.Volume: return 'objem';
    case Units.Stem: return 'stonek';
    case Units.Cup: return 'šálek';
    case Units.Mug: return 'hrnek';
    case Units.GlassOf: return 'sklenice';
    case Units.Cube: return 'kostka';
    case Units.Package: return 'balení';
    case Units.Bowl: return 'miska';
    case Units.Bottle: return 'láhev';
    default: return '';
  }
}

function getAllergenNames(allergens: Allergens): string[] {
  const allergenMap: { [key: number]: string } = {
    [Allergens.Gluten]: 'Lepek',
    [Allergens.Crustaceans]: 'Korýši',
    [Allergens.Eggs]: 'Vejce',
    [Allergens.Fish]: 'Ryby',
    [Allergens.Peanuts]: 'Arašídy',
    [Allergens.Soybeans]: 'Sója',
    [Allergens.Milk]: 'Mléko',
    [Allergens.Nuts]: 'Ořechy',
    [Allergens.Celery]: 'Celer',
    [Allergens.Mustard]: 'Hořčice',
    [Allergens.Sesame]: 'Sezam',
    [Allergens.Sulphites]: 'Siřičitany',
    [Allergens.Lupin]: 'Vlčí bob',
    [Allergens.Molluscs]: 'Měkkýši',
  };

  const names: string[] = [];
  Object.entries(allergenMap).forEach(([value, name]) => {
    const numValue = parseInt(value);
    if ((allergens & numValue) === numValue && numValue !== 0) {
      names.push(name);
    }
  });
  return names;
}

interface IngredientRowProps {
  ingredient: IngredientDto;
  onProductsChange: () => void;
  onIngredientChange: (ingredient: IngredientDto) => void;
  products: ProductDto[];
}

function IngredientRow({ ingredient, onProductsChange, onIngredientChange, products }: IngredientRowProps) {
  const [open, setOpen] = useState(false);
  const [linkedProducts, setLinkedProducts] = useState<IngredientProductDto[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [adding, setAdding] = useState(false);
  const [savingIsMeat, setSavingIsMeat] = useState(false);

  const handleIsMeatToggle = async (checked: boolean) => {
    try {
      setSavingIsMeat(true);
      await updateIngredient(ingredient.id, {
        id: ingredient.id,
        name: ingredient.name,
        amount: ingredient.amount,
        units: ingredient.units,
        allergens: ingredient.allergens,
        isMeat: checked,
      });
      onIngredientChange({ ...ingredient, isMeat: checked });
    } catch (err) {
      console.error('Error updating ingredient isMeat flag:', err);
    } finally {
      setSavingIsMeat(false);
    }
  };

  const fetchLinkedProducts = useCallback(async () => {
    try {
      setLoadingProducts(true);
      const data = await getIngredientProducts(ingredient.id);
      const normalized = Array.isArray(data)
        ? data
        : Array.isArray((data as { $values?: IngredientProductDto[] })?.$values)
          ? (data as { $values?: IngredientProductDto[] }).$values!
          : [];
      setLinkedProducts(normalized);
    } catch (err) {
      console.error('Error fetching ingredient products:', err);
    } finally {
      setLoadingProducts(false);
    }
  }, [ingredient.id]);

  useEffect(() => {
    if (open) {
      fetchLinkedProducts();
    }
  }, [open, fetchLinkedProducts]);

  const handleAddProduct = async (productId: string) => {
    try {
      setAdding(true);
      await addProductToIngredient(ingredient.id, productId);
      await fetchLinkedProducts();
      onProductsChange();
      setAddDialogOpen(false);
      setSearchTerm('');
    } catch (err) {
      console.error('Error adding product:', err);
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveProduct = async (ingredientProductId: string) => {
    try {
      await removeProductFromIngredient(ingredientProductId);
      await fetchLinkedProducts();
      onProductsChange();
    } catch (err) {
      console.error('Error removing product:', err);
    }
  };

  const linkedProductIds = linkedProducts.map(lp => lp.productId);
  const availableProducts = products.filter(
    p => !linkedProductIds.includes(p.id) && 
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const allergenNames = getAllergenNames(ingredient.allergens);

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {ingredient.name}
        </TableCell>
        <TableCell align="right">
          {ingredient.amount} {formatUnits(ingredient.units)}
        </TableCell>
        <TableCell align="center">
          <Switch
            size="small"
            checked={ingredient.isMeat}
            disabled={savingIsMeat}
            onChange={(e) => handleIsMeatToggle(e.target.checked)}
          />
        </TableCell>
        <TableCell>
          {allergenNames.length > 0 ? (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {allergenNames.map((name, idx) => (
                <Chip key={idx} label={name} size="small" color="warning" />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">-</Typography>
          )}
        </TableCell>
        <TableCell align="center">
          <Chip 
            label={linkedProducts.length} 
            size="small" 
            color={linkedProducts.length > 0 ? 'primary' : 'default'}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" gutterBottom component="div">
                  Přiřazené produkty
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  variant="outlined"
                  size="small"
                  onClick={() => setAddDialogOpen(true)}
                >
                  Přidat produkt
                </Button>
              </Box>
              
              {loadingProducts ? (
                <CircularProgress size={24} />
              ) : linkedProducts.length === 0 ? (
                <Alert severity="info">
                  Žádné produkty nejsou přiřazeny k této ingredienci.
                </Alert>
              ) : (
                <List dense>
                  {linkedProducts.map((lp) => (
                    <ListItem key={lp.id}>
                      <ListItemText primary={lp.productName || 'Unknown product'} />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleRemoveProduct(lp.id)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Add Product Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Přidat produkt k ingredienci: {ingredient.name}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Hledat produkt"
            fullWidth
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          
          {availableProducts.length === 0 ? (
            <Alert severity="info">
              {searchTerm ? 'Žádné produkty nenalezeny.' : 'Všechny produkty jsou již přiřazeny.'}
            </Alert>
          ) : (
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {availableProducts.slice(0, 20).map((product) => (
                <ListItem
                  key={product.id}
                  button
                  onClick={() => handleAddProduct(product.id)}
                  disabled={adding}
                >
                  <ListItemText primary={product.name} />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="add"
                      onClick={() => handleAddProduct(product.id)}
                      disabled={adding}
                      size="small"
                    >
                      <AddIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
              {availableProducts.length > 20 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                  Zobrazeno prvních 20 produktů. Použijte vyhledávání pro upřesnění.
                </Typography>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Zavřít</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function AdminIngredients() {
  const navigate = useNavigate();
  const [ingredients, setIngredients] = useState<IngredientDto[]>([]);
  const [products, setProducts] = useState<ProductDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Check if user is logged in
    if (!getAuthToken()) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        const [ingredientsData, productsData] = await Promise.all([
          getAllIngredients(),
          getAllProducts(),
        ]);

        // Normalize potential $values arrays
        const normalizedIngredients = Array.isArray(ingredientsData)
          ? ingredientsData
          : Array.isArray((ingredientsData as { $values?: IngredientDto[] })?.$values)
            ? (ingredientsData as { $values?: IngredientDto[] }).$values!
            : [];

        const normalizedProducts = Array.isArray(productsData)
          ? productsData
          : Array.isArray((productsData as { $values?: ProductDto[] })?.$values)
            ? (productsData as { $values?: ProductDto[] }).$values!
            : [];

        setIngredients(normalizedIngredients);
        setProducts(normalizedProducts);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please make sure you are logged in.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleProductsChange = () => {
    // Refresh if needed
  };

  const handleIngredientChange = (updated: IngredientDto) => {
    setIngredients(prev => prev.map(ing => (ing.id === updated.id ? updated : ing)));
  };

  const filteredIngredients = ingredients.filter(ing =>
    ing.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box m={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Správa ingrediencí
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Spravujte ingredience a jejich přiřazení k produktům.
      </Typography>

      <Paper sx={{ mb: 3, p: 2 }}>
        <TextField
          label="Hledat ingredience"
          fullWidth
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={50} />
              <TableCell>Název</TableCell>
              <TableCell align="right">Množství</TableCell>
              <TableCell align="center">Maso</TableCell>
              <TableCell>Alergeny</TableCell>
              <TableCell align="center">Produkty</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredIngredients.map((ingredient) => (
              <IngredientRow
                key={ingredient.id}
                ingredient={ingredient}
                onProductsChange={handleProductsChange}
                onIngredientChange={handleIngredientChange}
                products={products}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredIngredients.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {searchTerm ? 'Žádné ingredience nenalezeny.' : 'Žádné ingredience k dispozici.'}
        </Alert>
      )}
    </Box>
  );
}
